// Where a campaign variable takes its value from. Each source becomes the Liquid the send renders per
// contact, so nobody has to write `contact.custom_attribute.x` by hand to fill a template.
export const VARIABLE_SOURCES = {
  NAME: 'name',
  FIRST_NAME: 'first_name',
  PHONE: 'phone_number',
  EMAIL: 'email',
  COLUMN: 'column',
  TEXT: 'text',
};

export const variableToLiquid = ({ source, column, text }) => {
  if (source === VARIABLE_SOURCES.TEXT) return text;
  if (source === VARIABLE_SOURCES.COLUMN) {
    return column ? `{{ contact.custom_attribute.${column} }}` : '';
  }
  return source ? `{{ contact.${source} }}` : '';
};

// The name a spreadsheet header is filed under - the rule of Campaigns::AudienceUploadService, so a
// column named here is the one the upload writes.
export const normalizeColumnName = name =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\s\w]+/g, '')
    .trim()
    .replace(/\s+/g, '_');

const CUSTOM_ATTRIBUTE = /contact\.custom_attribute\.(\w+)/g;

// The spreadsheet columns some Liquid reads, in the order they first appear.
export const columnsInLiquid = values => [
  ...new Set(
    values.flatMap(value =>
      [...(value || '').matchAll(CUSTOM_ATTRIBUTE)].map(match => match[1])
    )
  ),
];

// The example Meta keeps for each variable of an approved template, so the spreadsheet comes with a
// realistic row. Positional examples are listed in order and named ones by name; a URL button's
// example is the whole link, and its variable is what comes after the fixed part.
export const variableExample = (template, { section, key, buttonIndex }) => {
  const find = type =>
    template.components?.find(component => component.type === type);

  if (section === 'buttons') {
    const button = find('BUTTONS')?.buttons?.[buttonIndex];
    const example = button?.example?.[0] || '';
    return button?.url ? example.slice(button.url.indexOf('{{')) : example;
  }

  const type = section === 'header' ? 'HEADER' : 'BODY';
  const prefix = section === 'header' ? 'header_text' : 'body_text';
  const example = find(type)?.example || {};
  const positional =
    section === 'header' ? example[prefix] : example[prefix]?.[0];
  return (
    example[`${prefix}_named_params`]?.find(param => param.param_name === key)
      ?.example ??
    positional?.[Number(key) - 1] ??
    ''
  );
};

// The words around a variable, so it is shown in the sentence it fills rather than as a bare number.
export const variableContext = (text, key, radius = 40) => {
  const match = new RegExp(`{{\\s*${key}\\s*}}`).exec(text || '');
  if (!match) return { before: '', after: '' };

  const flatten = value => value.replace(/\s+/g, ' ');
  let before = flatten(text.slice(0, match.index));
  let after = flatten(text.slice(match.index + match[0].length));
  if (before.length > radius) {
    before = `…${before.slice(-radius).replace(/^\S*\s/, '')}`;
  }
  if (after.length > radius) {
    after = `${after.slice(0, radius).replace(/\s\S*$/, '')}…`;
  }
  return { before, after };
};

const LIQUID_OUTPUT = /{{\s*([^}]+?)\s*}}/g;

// A text cut at its variables, so the preview can draw each one on its own: [{ text }, { key }, ...].
export const splitVariables = (text = '') => {
  const segments = [];
  let last = 0;
  [...text.matchAll(LIQUID_OUTPUT)].forEach(match => {
    if (match.index > last) {
      segments.push({ text: text.slice(last, match.index) });
    }
    segments.push({ key: match[1] });
    last = match.index + match[0].length;
  });
  if (last < text.length) segments.push({ text: text.slice(last) });
  return segments;
};
